import { Link } from "react-router-dom";
import { Trash2, Pencil, Copy, Eye, ShieldCheck, Clock } from "lucide-react";
import { formatINR, timeAgo, normalizeStatus } from "@/lib/quotationApi";
import { StatusSelect } from "@/components/StatusBadge";
import ActivityTimeline from "@/components/ActivityTimeline";

export default function HistoryCard({
  q,
  isManagerUp,
  can,
  onStatusChange,
  onDuplicate,
  duplicatingId,
  onDelete,
  activityFor,
  setActivityFor,
}) {
  return (
    <div className="card-luxe !p-4" data-testid={`history-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <Link to={`/quotations/${q.id}`} className="flex-1 min-w-0" data-testid={`history-open-${q.id}`}>
          <div className="font-display font-semibold text-[15px] flex items-center gap-2">
            {q.customer?.name || "Unnamed"}
            <Pencil size={12} className="text-[#C5A880]/60" />
          </div>
          <div className="text-xs text-white/40 mt-0.5">
            {q.customer?.mobile} ·{" "}
            {new Date(q.created_at).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
            {q.updated_at && (
              <span className="text-[#C5A880]/70"> · edited</span>
            )}
          </div>
          <div className="mt-2 text-[#C5A880] font-display font-bold text-xl">
            {formatINR(q.grand_total)}
          </div>
          {q.last_viewed_at && (
            <div
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-emerald-400/90"
              data-testid={`history-viewed-${q.id}`}
            >
              <Eye size={11} /> Viewed {timeAgo(q.last_viewed_at)}
              {q.view_count > 1 ? ` · ${q.view_count}×` : ""}
            </div>
          )}
          {isManagerUp && normalizeStatus(q.status) === "approved" && q.approved_by_name && (
            <div
              className="mt-1.5 flex items-center gap-1 text-[11px] text-[#C5A880]/90"
              data-testid={`history-approval-trail-${q.id}`}
            >
              <ShieldCheck size={11} /> Approved by {q.approved_by_name}
              {q.approved_at && (
                <> · {new Date(q.approved_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</>
              )}
            </div>
          )}
        </Link>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <StatusSelect
            status={q.status}
            testId={`history-status-${q.id}`}
            disabled={!can("quotations.status") && !can("quotations.approve")}
            canApprove={can("quotations.approve")}
            onChange={(s) => onStatusChange(q.id, s)}
          />
          <div className="flex items-center">
            <button
              onClick={() => onDuplicate(q.id)}
              disabled={duplicatingId === q.id || !can("quotations.edit")}
              className="text-white/30 hover:text-[#C5A880] p-2 disabled:opacity-40 disabled:hover:text-white/30"
              data-testid={`history-duplicate-${q.id}`}
              aria-label="Duplicate with current prices"
              title="Duplicate with today's prices"
            >
              <Copy size={16} />
            </button>
            {can("quotations.delete") && (
              <button
                onClick={() => onDelete(q.id)}
                className="text-white/30 hover:text-red-400 p-2"
                data-testid={`history-delete-${q.id}`}
                aria-label="Delete"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      {isManagerUp && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <button
            onClick={() => setActivityFor(activityFor === q.id ? null : q.id)}
            className="text-[11px] text-white/40 hover:text-[#C5A880] flex items-center gap-1.5"
            data-testid={`history-activity-toggle-${q.id}`}
          >
            <Clock size={12} />
            {activityFor === q.id ? "Hide activity" : "View activity"} ({(q.activity || []).length})
          </button>
          {activityFor === q.id && (
            <div className="mt-3">
              <ActivityTimeline activity={q.activity || []} testId={`history-activity-${q.id}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
